/**
 * おたんじょうび(とし と つき だけ)。docs/12_challenge_ranking.md §1。
 *
 *   「プロフィールは **生まれ年+月のみ** 保持(誕生日フルは持たない。PII最小化)」
 *
 * 使いみちは**チャレンジのランキングを年齢別に分けること**だけ。
 * 入れなくてもチャレンジは遊べる(「ぜんたい」のランキングにだけ載る)ので、
 * 文言でも「いれなくても だいじょうぶ」とはっきり伝える。
 *
 * 保存先はサーバー(`updateProfile` callable)。年齢帯はランキングの根拠なので
 * クライアントからは直接書けない(`firestore.rules` で deny)。
 * 端末にも控えを残して、次に開いたときすぐ出せるようにする。
 */
import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import './language.css';

/** 選べる「とし」の範囲(いまの年から何年ぶん さかのぼるか)。 */
export const BIRTH_YEAR_SPAN = 20;

/** `2017-04` → `{year: 2017, month: 4}`。壊れていたら未選択。 */
export function splitBirthYearMonth(value: string | null): {
  readonly year: string;
  readonly month: string;
} {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return { year: '', month: '' };
  return { year: value.slice(0, 4), month: String(Number(value.slice(5, 7))) };
}

/** 年と月がそろっていれば `YYYY-MM`、片方でも欠けていれば `null`。 */
export function joinBirthYearMonth(year: string, month: string): string | null {
  if (!year || !month) return null;
  return `${year}-${String(Number(month)).padStart(2, '0')}`;
}

/** いまの年から古い順に並べた選択肢。 */
export function birthYearOptions(now: Date = new Date()): number[] {
  const current = now.getFullYear();
  return Array.from({ length: BIRTH_YEAR_SPAN }, (_, i) => current - i);
}

interface Props {
  /** いま保存されている値(`null` = 未設定)。 */
  readonly value: string | null;
  /** 変更を保存する。`null` は「けす」。 */
  readonly onSave: (value: string | null) => void;
}

/** 状態を持たない見た目だけの本体(テストしやすいように分けてある)。 */
export function BirthdaySettingsView({ value, onSave }: Props) {
  const initial = splitBirthYearMonth(value);
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);

  // 外から値が入れ替わったら(サーバーから読み直したら)追随する。
  useEffect(() => {
    const next = splitBirthYearMonth(value);
    setYear(next.year);
    setMonth(next.month);
  }, [value]);

  const commit = (nextYear: string, nextMonth: string): void => {
    setYear(nextYear);
    setMonth(nextMonth);
    onSave(joinBirthYearMonth(nextYear, nextMonth));
  };

  return (
    <section className="card language-settings birthday-settings">
      <h2 className="card-title">🎂 おたんじょうび</h2>
      <p className="lang-note">
        うまれた <b>とし</b> と <b>つき</b> だけ おしえてね。チャレンジの ランキングで、 おなじ
        としの ともだちと くらべる ために つかうよ。
        <br />
        いれなくても だいじょうぶ。そのときは「ぜんたい」の ランキングに でるよ。
        {/* 保護者向け: 日付は保存しません(PII最小化。docs/12 §1)。 */}
      </p>

      <div className="lang-choices birthday-row">
        <label className="birthday-field">
          <span className="birthday-label">とし</span>
          <select
            className="birthday-select"
            name="birth-year"
            value={year}
            onChange={(event) => commit(event.target.value, month)}
          >
            <option value="">えらんでね</option>
            {birthYearOptions().map((option) => (
              <option key={option} value={String(option)}>
                {option}ねん
              </option>
            ))}
          </select>
        </label>

        <label className="birthday-field">
          <span className="birthday-label">つき</span>
          <select
            className="birthday-select"
            name="birth-month"
            value={month}
            onChange={(event) => commit(year, event.target.value)}
          >
            <option value="">えらんでね</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((option) => (
              <option key={option} value={String(option)}>
                {option}がつ
              </option>
            ))}
          </select>
        </label>

        {year || month ? (
          <button type="button" className="birthday-clear" onClick={() => commit('', '')}>
            けす
          </button>
        ) : null}
      </div>
    </section>
  );
}

/**
 * 画面に置くほう。
 *
 * `gameStore` の設定に控えを持ち、同じ値をサーバーへも送る
 * (ローカルモードでは送信先が無いので控えだけが残る)。
 */
export function BirthdaySettings() {
  const birthYearMonth = useGameStore((state) => state.settings.birthYearMonth);
  const setBirthYearMonth = useGameStore((state) => state.setBirthYearMonth);
  return <BirthdaySettingsView value={birthYearMonth} onSave={setBirthYearMonth} />;
}
